import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import isEmail from 'validator/lib/isEmail';

import ContactInput from '../components/ContactInput';
import config from '../aws/config.json';

import './ContactForm.css';

window.AWS.config.update({
  region: config.REGION
});
window.AWS.config.credentials = new window.AWS.CognitoIdentityCredentials({
  IdentityPoolId: config.COGNITO.IDENTITY_POOL_ID
});

const lambda = new window.AWS.Lambda({
  region: config.REGION,
  apiVersion: '2015-03-31'
});

const SENDING_STATE = {
  IDLE: 'idle',
  SENDING: 'sending',
  SENT: 'sent'
};

const INITIAL_STATE = {
  // Inputs
  name: '',
  email: '',
  title: '',
  message: '',
  // etc: '',
  // Other states
  errorInputs: {},
  serviceError: false,
  sendingState: SENDING_STATE.IDLE
};

const validators = {
  name: val => {
    if (val === '') {
      return <FormattedMessage id="ContactForm.requiredError"/>
    }
    return null;
  },
  email: val => {
    if (val === '') {
      return <FormattedMessage id="ContactForm.requiredError"/>
    } else if (!isEmail(val)) {
      return <FormattedMessage id="ContactForm.invalidEmailError"/>
    }
    return null;
  },
  title: val => {
    return null;
  },
  message: val => {
    return null;
  },
};

export default class ContactForm extends React.Component {

  static contextTypes = {
    intl: PropTypes.object.isRequired
  };
  
  static propTypes = {
    className: PropTypes.string
  };

  state = INITIAL_STATE;

  render() {
    const inputs = [
      {
        id: `name`,
        type: 'TEXT',
        required: true,
        title: this.context.intl.formatMessage({ id: 'ContactForm.name' }),
        error: this.state.errorInputs[`name`],
        maxLength: 254,
        value: this.state.name,
      },
      {
        id: `email`,
        type: 'EMAIL',
        required: true,
        title: this.context.intl.formatMessage({ id: 'ContactForm.email' }),
        error: this.state.errorInputs[`email`],
        maxLength: 254,
        value: this.state.email,
      },
      {
        id: `title`,
        type: 'TEXT',
        required: false,
        title: this.context.intl.formatMessage({ id: 'ContactForm.title' }),
        error: this.state.errorInputs[`title`],
        maxLength: 254,
        value: this.state.title,
      },
      {
        id: `message`,
        type: 'MESSAGE',
        required: false,
        title: this.context.intl.formatMessage({ id: 'ContactForm.message' }),
        error: this.state.errorInputs[`message`],
        maxLength: 1024,
        value: this.state.message,
      },
    ];
    const inputMap = inputs.reduce((result, input) => ({
      ...result,
      [input.id]: input
    }), {});

    return (
      <div
        className={classnames('ContactForm', this.props.className, {
        [this.state.sendingState]: true
        })}
        ref={(input) => { this._form = input; }}>
        <div className="ContactForm__content">
          <form className="ContactForm__form">
            {
              inputs.map((props, idx) => (
                <ContactInput
                  key={idx}
                  className="ContactForm__input"
                  {...props}
                  onChangeInput={this._onChangeInput}
                />
              ))
            }
            <button
              className="ContactForm__sendBtn"
              type="submit"
              onClick={this._onClickSendBtn}>
              <FormattedMessage id="ContactForm.send"/>
            </button>
            {
              this.state.serviceError ? (
                <div className="ContactForm__hasError">
                  <FormattedMessage id="ContactForm.serviceError"/>
                </div>
              ) : (
                Object.keys(this.state.errorInputs).length > 0 &&
                <div className="ContactForm__hasError">
                  <FormattedMessage
                    id="ContactForm.hasError"
                    values={{
                      inputs: Object.keys(this.state.errorInputs).map(id => `"${inputMap[id].title}"`).join(`, `)
                    }}
                  />
                </div>
              )
            }
          </form>
        </div>
        <div className="ContactForm__content2">
          <div
            className="ContactForm__spinnerContainer">
            <div className="ContactForm__spinner">
              <div className="ContactForm__spinnerDone">
                <div className="ContactForm__spinnerCheck">
                  <svg xmlns="http://www.w3.org/2000/svg" width="31" height="22" viewBox="0 0 31 22">
                    <path fillRule="evenodd" d="M671.536443,405.719631 C670.829314,404.871077 669.568185,404.756429 668.719631,405.463557 C667.871077,406.170686 667.756429,407.431815 668.463557,408.280369 L679.865603,421.962824 L698.414214,403.414214 C699.195262,402.633165 699.195262,401.366835 698.414214,400.585786 C697.633165,399.804738 696.366835,399.804738 695.585786,400.585786 L680.134397,416.037176 L671.536443,405.719631 Z" transform="translate(-668 -400)"/>
                  </svg>
                </div>
              </div>
            </div>
            <div className="ContactForm__sendingStatusContainer">
              <div className="ContactForm__sent">
                <FormattedMessage 
                  id="ContactForm.messageSent"
                  values={{
                    email: <strong>{this.state.email}</strong>
                  }}
                />
              </div>
              <div
                className="ContactForm__newMessage"
                onClick={this._onClickNewMessageBtn}>
                <FormattedMessage id="ContactForm.tryNewMessage"/>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  _onChangeInput = (input, evt) => {
    const errorInputs = {...this.state.errorInputs};
    delete errorInputs[input];

    this.setState({
      [input]: evt.target.value,
      errorInputs,
      serviceError: false
    });
  };

  _onClickSendBtn = evt => {
    evt.preventDefault();

    const errorInputs = {};
    Object.keys(validators).forEach(id => {
      const value = this.state[id];
      const validator = validators[id];
      const result = validator(value);

      if (result) {
        errorInputs[id] = result;
      }
    });
    if (Object.keys(errorInputs).length > 0) {
      this.setState({
        errorInputs,
        serviceError: false
      });
      return;
    }

    this.setState({
      sendingState: SENDING_STATE.SENDING
    });

    var top = this._form.offsetTop;
    window.scrollTo(0, top - 120 - 140);

    // create variable to hold data returned by the Lambda function
    lambda.invoke({
      FunctionName : config.LAMBDA.FUNCTION_NAME,
      InvocationType : 'RequestResponse',
      LogType : 'None',
      Payload: JSON.stringify({
        name: this.state.name,
        email: this.state.email,
        title: this.state.title,
        message: this.state.message,
      })
    }, (error, data) => {
      if (error || !data || (data.Payload && JSON.parse(data.Payload).hasOwnProperty('errorMessage'))) {
        this.setState({
          sendingState: SENDING_STATE.IDLE,
          serviceError: true
        });
      } else {
        this.setState({
          sendingState: SENDING_STATE.SENT,
          serviceError: false
        });
      }
    });
  };

  _onClickNewMessageBtn = () => {
    this.setState(INITIAL_STATE);
  };

}


// WEBPACK FOOTER //
// ./src/components/ContactForm.js